import React from "react";
import { Slider } from "@mui/material";

// Labels and values for duration options
const DURATION_MARKS = [
  { value: 30, label: "30m" },
  { value: 45, label: "45m" },
  { value: 60, label: "1h" },
  { value: 90, label: "1h30m" },
  { value: 120, label: "2h" },
  { value: 180, label: "3h" },
  { value: 240, label: "4h" },
  { value: 480, label: "8h" },
  { value: 2880, label: "2D" },
  { value: 4320, label: "3D" },
] as const;

// Derived arrays for equal-distance slider behavior
const DURATION_OPTIONS = DURATION_MARKS.map(
  (m) => m.value,
) as readonly number[];
const EQUAL_INDEX_MARKS = DURATION_MARKS.map((m, idx) => ({
  value: idx,
  label: m.label,
}));

function formatDuration(totalMinutes: number): string {
  if (!totalMinutes) return "";
  if (totalMinutes >= 1440) {
    const days = Math.round(totalMinutes / 1440);
    return `${days} ${days === 1 ? "day" : "days"}`;
  }
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours && minutes) return `${hours}h ${minutes}m`;
  if (hours) return `${hours}h`;
  return `${minutes}m`;
}

function minutesToIndex(minutes?: number): number {
  if (!minutes) return 0;
  const idx = DURATION_OPTIONS.indexOf(
    minutes as (typeof DURATION_OPTIONS)[number],
  );
  if (idx !== -1) return idx;
  // Fallback: choose closest
  let closest = 0;
  let diff = Infinity;
  DURATION_OPTIONS.forEach((opt, i) => {
    const d = Math.abs(opt - (minutes as number));
    if (d < diff) {
      diff = d;
      closest = i;
    }
  });
  return closest;
}

export interface WebinarDurationSliderProps {
  valueMinutes?: number | null;
  onChangeMinutes: (minutes: number) => void;
  ariaLabel?: string;
  disabled?: boolean;
}

export default function WebinarDurationSlider({
  valueMinutes,
  onChangeMinutes,
  ariaLabel = "Event Duration",
  disabled,
}: WebinarDurationSliderProps) {
  return (
    <Slider
      getAriaLabel={() => ariaLabel}
      valueLabelDisplay="on"
      valueLabelFormat={(v) => formatDuration(DURATION_OPTIONS[v as number])}
      getAriaValueText={(v) => formatDuration(DURATION_OPTIONS[v as number])}
      value={minutesToIndex(valueMinutes ?? undefined)}
      onChange={(_, v) => onChangeMinutes(DURATION_OPTIONS[v as number])}
      step={1}
      marks={EQUAL_INDEX_MARKS}
      min={0}
      max={DURATION_OPTIONS.length - 1}
      sx={{
        mt: 1,
        '& .MuiSlider-markLabel[data-index="0"]': {
          transform: "translateX(0%) !important",
          textAlign: "right",
        },
        "& .MuiSlider-markLabel:last-of-type": {
          transform: "translateX(0%)",
          textAlign: "left",
        },
      }}
      disabled={disabled}
    />
  );
}
